import React, { FC, Fragment, useState } from 'react';
import { TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper } from '@mui/material';
import { User } from 'models/user';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import DelayedLinearProgress from 'components/shared/delayed-linear-progress/delayed-linear-progress.component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';
import UserItem from './user-item';
import UpdateUserDialog from '../update-user-dialog/update-user-dialog';

type UserListProps = {
    filteredUsers: User[];
    onFetchMore: () => void;
    usersLoading: boolean;
    onUserUpdated: (user: User) => void;
    onLoginClick?: (user: User) => void;
    onGenerateOtpClick?: (user: User) => void;
    refetchUsers?: () => void;
};

const UserList: FC<UserListProps> = ({
    filteredUsers,
    onFetchMore,
    usersLoading,
    onUserUpdated,
    onLoginClick,
    onGenerateOtpClick,
    refetchUsers,
}: UserListProps) => {
    const { t: translate } = useTranslation(['common']);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [userToUpdate, setUserToUpdate] = useState<User>({} as User);

    const handleUpdateUser = (user: User) => {
        setUserToUpdate(user);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(onFetchMore, filteredUsers?.length || 0);

    return (
        <Fragment>
            {usersLoading ? <DelayedLinearProgress /> : undefined}
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={filteredUsers ? filteredUsers.length : 0}
                    next={onFetchMore}
                    hasMore={true}
                    throttleTimeInMs={75}
                    loader={undefined}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                {/* <TableCell align="center">{translate('common:STATUS')}</TableCell> */}
                                <TableCell>{translate('common:NAME')}</TableCell>
                                <TableCell>{translate('common:EMAIL')}</TableCell>
                                <TableCell>{translate('common:ROLE')}</TableCell>
                                {/* <TableCell>{translate('common:TYPE')}</TableCell> */}
                                <TableCell>{translate('common:ORGANIZATION')}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredUsers ? (
                                <Fragment>
                                    {filteredUsers.map((user: User) => (
                                        <UserItem
                                            key={user._id}
                                            user={user}
                                            onUpdateUser={handleUpdateUser}
                                            onLoginClick={onLoginClick}
                                            onGenerateOtpClick={onGenerateOtpClick}
                                        />
                                    ))}
                                </Fragment>
                            ) : undefined}
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>
            <UpdateUserDialog
                dialogOpen={dialogOpen}
                onDialogClose={handleCloseDialog}
                userToUpdate={userToUpdate}
                onUserUpdated={onUserUpdated}
                refetchUsers={refetchUsers}
            />
        </Fragment>
    );
};

export default UserList;
